// CSS
import './Bookmarks.css';
// React hooks
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Global hooks
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
// Skybase components
import Sidebar from '../../components/Sidebar/Sidebar';
import BookmarkInstance from './BookmarkInstance/BookmarkInstance';
import DocumentPreviewModal from '../../components/Modals/FullScreenModal/DocumentPreviewModal/DocumentPreviewModal';
import FullScreenModal from '../../components/Modals/FullScreenModal/FullScreenModal';
import InformationalPopover from '../../components/Popovers/InformationalPopover/InformationalPopover';
// External hooks/components
import { useAuth as useClerkAuth } from '@clerk/react';


// =========
// Bookmarks – represents the /bookmarks Product page.
// =========
function Bookmarks(){
    
    // destructure global context
    const { activeEngineBookmarkJson } = useData();
    const { isSubscribed, checkIsSubscribed, activeUserJson } = useAuth();
    const { isLoading: isClerkLoading, getToken } = useClerkAuth();

    // init hooks
    const navigate = useNavigate('');

    // state vars
    const [isDocumentPreviewModalOpen, setIsDocumentPreviewModalOpen] = useState(false);
    const [targetDocument, setTargetDocument] = useState(null); // default: NULL

    // ================
    // onMount Behavior
    // ================
    // onMount --> check for valid Skybase Stripe subscription
    const [isInformationalPopoverOpen, setIsInformationalPopoverOpen] = useState(false);
    useEffect(() => {
        
        
        // Async. fn that prevents CurrentUsers without an active subscription from interacting with this page.
        const checkSubscription = async () => {
            // Don't perform a subscription-check if the Clerk User hasn't loaded yet.
            if (isClerkLoading) return;

            // Does CurrentUser have an active subscription?
            const subscribed = await checkIsSubscribed();

            // If NO: Display InformationalPopover.
            if (!subscribed) { setIsInformationalPopoverOpen(true); }
        };

        // Run the async. subscription-check
        checkSubscription();

        // Edge case: "bfcache === true" --> Forcibly reload the page to re-run the async. subscription-check
        const handlePageShow = (event) => {
            if (event.persisted) { window.location.reload(); }
        };
        window.addEventListener("pageshow", handlePageShow); // "pageShow" event listener

        // Edge case: No <Clerk JWT> --> Immediately redirect user from this page.
        checkForValidClerkTokenOtherwiseRedirect();

        // Remove listeners onDismount (prevent memory overflow)
        return () => { window.removeEventListener("pageshow", handlePageShow); };

    }, [isClerkLoading])


    // ==========================================
    // checkForValidClerkTokenOtherwiseRedirect()
    // ==========================================
    // Checks for a valid clerk token. Forcibly re-directs the user to '/login' if not.
    async function checkForValidClerkTokenOtherwiseRedirect() {
        let token = await getToken();
        if (!token) { navigate('../login') };
    }
    // onChange ('activeUserJson') --> immediately check again for valid subscription
    useEffect(() => {
        checkForValidClerkTokenOtherwiseRedirect();
    }, [activeUserJson]);


    // ==================
    // DIR INSTANCE CLICK
    // ==================
    const handleBookmarkClick = (id) => {
        
        // open the target Document
        setTargetDocument(id);
        setIsDocumentPreviewModalOpen(true);
    }
    
    // JSX Component
    return(
        <div className='Layout'>
            
            {/* Sidebar */}
            <Sidebar/>

            {/* Main content */}
            <div className='Bookmarks'>

                {/* Repeater header */}
                <div className='Directory-repeater-header SearchPage-fade-in'>
                    
                    
                    {/* Buffer */}
                    <div className='Directory-repeater-header-label--buffer'></div>

                    {/* Repeater label names */}
                    <div className='Directory-repeater-header-label--name'>
                        <div>Name</div>
                        <div>Last Modified</div>
                    </div>
                </div>

                {/* Repeater */}
                <div className='Directory-repeater'>
                    
                    {/* ..elements */}
                    {activeEngineBookmarkJson.map((bookmark, index) => (
                        <div key={bookmark.id} onClick={() => handleBookmarkClick(bookmark.item_id)} className='Directory-repeater-instance' style={{ "--stagger-index": Math.min(index, 0) }}>
                            <BookmarkInstance documentID={bookmark.item_id}/>
                        </div>
                    ))}


                    {/* Edge case: No bookmarks in this engine */}
                    {activeEngineBookmarkJson?.length === 0 && <>
                        <span className='History-repeater--empty-text'>
                            <span className='SearchPage-fade-in'>You do not have any bookmarks saved in this database.</span>
                        </span>
                    </>}
                </div>
            </div>


            {/* DocumentPreviewModal */}
            <FullScreenModal isOpen={isDocumentPreviewModalOpen} onClose={() => setIsDocumentPreviewModalOpen(false)} children={<DocumentPreviewModal uuid={targetDocument} isReadOnly={true} triggerClose={() => setIsDocumentPreviewModalOpen(false)} />}/>

            {/* InformationalPopover */}
            { isInformationalPopoverOpen &&
                <InformationalPopover
                    titleText={"Subscription Required"}
                    bodyText={"Please purchase a Skybase subscription plan to continue."}
                    primaryButtonText={"View Subscription Plans"}
                    primaryButtonCallback={() => navigate('../pricing')}
                    secondaryButtonText={"Return Home"}
                    secondaryButtonCallback={() => navigate('../')}
                />
            }
        </div>
    );
};


// Export statement
export default Bookmarks;